/**
 * Encodes an S3 key (which may contain slashes) into a URL-safe base64 string.
 * This allows passing S3 paths like "uploads/files/uuid/filename.png" as a single URL segment.
 */
export function encodeS3Key(key: string): string {
  // Use base64url encoding (URL-safe variant of base64)
  // Replace + with -, / with _, and remove = padding
  const base64 = btoa(key)
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Decodes a base64url-encoded S3 key back to the original path.
 */
export function decodeS3Key(encoded: string): string {
  // Restore base64 characters and add padding if needed
  let base64 = encoded.replace(/-/g, '+').replace(/_/g, '/')
  // Add padding if necessary
  const padding = base64.length % 4
  if (padding) {
    base64 += '='.repeat(4 - padding)
  }
  return atob(base64)
}


export function buildImageUrl(
  input: string,
  options: {
    w?: number
    h?: number
    output_format?: 'webp' | 'jpeg' | 'png' | 'avif'
    fit?: 'cover' | 'contain' | 'scale-down' | 'pad'
    quality?: number
  } = {}
): string {
  const { w = 283, h = 283, output_format = 'webp', fit = 'cover', quality } = options

  try {
    let s3Key: string

    // Case 1: Already a resize-worker URL - extract the encoded key
    const workerMatch = input.match(/^https?:\/\/resize-worker\.revelstreet\.co\/images\/([^?]+)/i)
    if (workerMatch) {
      s3Key = decodeS3Key(workerMatch[1])
    }
    // Case 2: Full S3 URL - extract the key
    else if (/^https?:\/\/revelstreet-media-production\.s3\.amazonaws\.com\//i.test(input)) {
      const s3Match = input.match(/^https?:\/\/revelstreet-media-production\.s3\.amazonaws\.com\/(.+)/i)
      if (!s3Match) return input
      s3Key = decodeURIComponent(s3Match[1])
    }
    // Case 3: Other full URLs - return unchanged
    else if (/^https?:\/\//i.test(input)) {
      return input
    }
    // Case 4: S3 key (not a URL)
    else {
      s3Key = input
    }

    // Normalize key (strip media/ prefix if present)
    const normalizedKey = s3Key.startsWith('media/') ? s3Key.slice('media/'.length) : s3Key

    const encodedKey = encodeS3Key(normalizedKey)
    const params = new URLSearchParams({
      w: String(w),
      h: String(h),
      output_format,
      fit,
    })
    if (quality !== undefined) {
      params.set('quality', String(quality))
    }
    return `https://resize-worker.revelstreet.co/images/${encodedKey}?${params.toString()}`
  } catch {
    return input
  }
}